import React, { useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { getFontSize } from "../../AppFont";
import { FullScreenLoader } from "../../components/full-screen-loader/full-screen-loader";
import { ChapterEntity } from "../../generated/graphql";
import { useInitiatives } from "./home2.hooks";
import { WelcomeHelpCard } from "./components/welcome-help-card/welcome-help-card";
import { InitiativeListCard } from "./components/initiative-list-card/initiative-list-card";
import { ReferenceLibraryCard } from "./components/reference-library-card/reference-library-card";

function sortReferenceLibraryChapters(
  chapters: ChapterEntity[],
  refdocCount: { [k: string]: number },
  initiativeNameSequence: string[]
): ChapterEntity[] {
  const filtered = chapters.map((chapter) => ({
    ...chapter,
    refdocs: chapter.refdocs
      .filter((doc) => !doc.is_hidden_in_ih)
      .slice()
      .sort((a, b) => {
        if (!refdocCount[String(a.id)]) return 1;
        if (!refdocCount[String(b.id)]) return -1;
        return refdocCount[String(a.id)] > refdocCount[String(b.id)] ? -1 : 1;
      }),
  }));

  return filtered.slice().sort((a, b) => {
    const aIndex = initiativeNameSequence.indexOf(a.chapter_name);
    const bIndex = initiativeNameSequence.indexOf(b.chapter_name);
    if (aIndex !== -1 || bIndex !== -1) {
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    }
    const totalA = a.refdocs.reduce(
      (sum, doc) => sum + (refdocCount[String(doc.id)] ?? 0),
      0
    );
    const totalB = b.refdocs.reduce(
      (sum, doc) => sum + (refdocCount[String(doc.id)] ?? 0),
      0
    );
    return totalA > totalB ? -1 : 1;
  });
}

export const HomeScreen2 = () => {
  const [searchParams] = useSearchParams();
  const refChapterId = searchParams.get("ref_cid");
  
  const {
    loading,
    error,
    initiativePackageGroups,
    initiativesWithPackageName,
    referenceDocuments,
    learnerFirstName,
    refdocCountByDocumentId,
    refetch,
  } = useInitiatives();

  // All hooks must run before any conditional return (Rules of Hooks)
  const refdocCount = useMemo((): { [k: string]: number } => {
    const out: { [k: string]: number } = {};
    Object.entries(refdocCountByDocumentId).forEach(([id, count]) => {
      out[id] = count;
    });
    return out;
  }, [refdocCountByDocumentId]);

  const initiativeNameSequence = useMemo(
    () =>
      initiativesWithPackageName
        .filter((x) => x.initiative.questionCount > 0)
        .map((x) => x.initiative.unit.name),
    [initiativesWithPackageName]
  );

  const sortedReferenceChapters = useMemo(
    () =>
      sortReferenceLibraryChapters(
        referenceDocuments,
        refdocCount,
        initiativeNameSequence
      ),
    [referenceDocuments, refdocCount, initiativeNameSequence]
  );

  const packageGroupsFiltered = useMemo(
    () =>
      initiativePackageGroups
        .map((group) => ({
          ...group,
          initiatives: group.initiatives.filter((i) => i.questionCount > 0),
        }))
        .filter((group) => group.initiatives.length > 0),
    [initiativePackageGroups]
  );

  if (loading) return <FullScreenLoader />;
  if (error) return <Navigate to="/login" replace />;

  return (
    <div
      style={{
        background: "rgb(234, 234, 241)",
        fontSize: getFontSize(),
      }}
      className="overflow-auto flex-1 min-w-0 px-4 pb-8"
    >
      <div className="pt-8">
        <WelcomeHelpCard firstName={learnerFirstName} refdocCount={refdocCount} />
      </div>
      <div className="mt-4">
        <InitiativeListCard
          packageGroups={packageGroupsFiltered}
          refetchInitiatives={refetch}
        />
      </div>
      <div className="mt-5">
        <ReferenceLibraryCard 
          chapters={sortedReferenceChapters} 
          refdocCount={refdocCount} 
          initialExpandedChapterId={refChapterId ? parseInt(refChapterId, 10) : undefined}
        />
      </div>
    </div>
  );
};
